import React from 'react';
import moment from 'moment';
import './SnoozedTasksModal.css';

interface Task {
    id: string;
    title: string;
    categoryId: string;
    urgency: 'high' | 'medium' | 'low' | null;
    snoozedUntil?: number;
}

interface Category {
    id: string;
    name: string;
}

interface SnoozedTasksModalProps {
    isOpen: boolean;
    onClose: () => void;
    tasks: Task[];
    categories: Category[];
    onUnsnooze: (taskId: string) => void;
}

export const SnoozedTasksModal: React.FC<SnoozedTasksModalProps> = ({
    isOpen,
    onClose,
    tasks,
    categories,
    onUnsnooze
}) => {
    if (!isOpen) return null;

    const getUrgencyEmoji = (urgency: 'high' | 'medium' | 'low' | null) => {
        switch (urgency) {
            case 'high': return '❗️';
            case 'medium': return '⚠️';
            case 'low': return 'ℹ️';
            default: return null;
        }
    };

    const formatSnoozeTime = (timestamp: number) => {
        const snoozeMoment = moment(timestamp);
        let relative: string;

        // Use calendar time for up to a week out
        relative = snoozeMoment.calendar(undefined, {
            sameDay: '[today]',
            nextDay: '[tomorrow]',
            nextWeek: 'dddd',
            lastDay: '[yesterday]',
            lastWeek: '[last] dddd',
            sameElse: 'dddd MMM D, YYYY'
        });

        // Only show full date in parentheses if we're using a relative format
        const isRelativeFormat = !relative.includes(snoozeMoment.format('MMM'));
        return isRelativeFormat
            ? `${relative} (${snoozeMoment.format('MMM D, YYYY')})`
            : relative;
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content snoozed-tasks-modal" onClick={e => e.stopPropagation()}>
                <h2>Snoozed Tasks</h2>
                <div className="snoozed-tasks-list">
                    {tasks.length > 0 ? (
                        tasks.map(task => (
                            <div key={task.id} className="snoozed-task-item">
                                <div className="snoozed-task-info">
                                    <div className="snoozed-task-title-row">
                                        <span className="snoozed-task-urgency">
                                            {getUrgencyEmoji(task.urgency)}
                                        </span>
                                        <span className="snoozed-task-title">{task.title}</span>
                                    </div>
                                    <div className="snoozed-task-meta-row">
                                        <span className="snoozed-task-category">
                                            {categories.find(c => c.id === task.categoryId)?.name || 'Uncategorized'}
                                        </span>
                                        <span className="snoozed-task-time">
                                            Until {formatSnoozeTime(task.snoozedUntil!)}
                                        </span>
                                    </div>
                                </div>
                                <button
                                    className="unsnooze-btn"
                                    onClick={() => onUnsnooze(task.id)}
                                >
                                    Unsnooze
                                </button>
                            </div>
                        ))
                    ) : (
                        <div className="no-snoozed-tasks">
                            No snoozed tasks
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}; 